import type { DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ZfInput, ZfStack, ZfTypography } from '@zafire/react';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DroppableId } from '../../../enums';
import type { IDndItem } from '../../../interfaces/dnd';
import { Draggable } from './Draggable';
import { Droppable } from './Droppable';

const dndListStyle: CSSProperties = {
    flex: 1,
    height: '500px',
    padding: '4px',
    overflowY: 'auto',
};

interface IProps<TItem> {
    allItems: (IDndItem & TItem)[];
    selectedItems: (IDndItem & TItem)[];
    filterFn?: (item: IDndItem & TItem, filterTerm: string) => boolean;
    itemRenderFn: (item: IDndItem & TItem) => ReactNode;
    onItemsChange: (items: (IDndItem & TItem)[]) => void;
    sortAvailableItems: boolean;
}

export const Dnd = <TItem extends IDndItem>({ allItems, selectedItems, filterFn, itemRenderFn, onItemsChange, sortAvailableItems }: IProps<TItem>) => {
    const { i18n, t } = useTranslation();

    const sortFn = (a: IDndItem, b: IDndItem) => {
        const itemA = typeof a.name === 'string' ? a.name : a.name[i18n.language];
        const itemB = typeof b.name === 'string' ? b.name : b.name[i18n.language];

        return itemA.localeCompare(itemB);
    };

    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const [selected, setSelected] = useState([...selectedItems]);
    const [available, setAvailable] = useState(
        allItems.filter((item: IDndItem & TItem) => !selectedItems.find((selItem) => selItem.id === item.id)).sort(sortFn),
    );

    const onFilterChange = (e: CustomEvent<string>) => {
        if (filterFn) {
            setAvailable(
                allItems
                    .filter((item) => !selectedItems.some((selItem) => selItem.id === item.id))
                    .filter((avItem) => filterFn(avItem, e.detail.toLowerCase())),
            );
        }
    };

    const setAvailableItems = (items: (IDndItem & TItem)[]) => {
        setAvailable(sortAvailableItems ? [...items].sort(sortFn) : items);
    };

    const onDragStart = (event: DragStartEvent) => setActiveId(event.active.id);

    const onDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;
        const activeIdNum = Number(active.id);
        const overId = over?.id ? (over.id as DroppableId) : undefined;
        const activeItem = allItems.find((item) => item.id === activeIdNum);
        const isActiveInSelected = selected.some((item) => item.id === activeIdNum);
        const isOverSelected = overId === DroppableId.Selected || selected.some((item) => item.id.toString() === overId);
        const isOverAvailable = overId === DroppableId.Available || available.some((item) => item.id.toString() === overId);

        // Dropped outside the list or dropped available item in available list
        if (!activeItem || !over || (!isActiveInSelected && isOverAvailable)) return;

        // Reordering within "Selected items"
        if (isActiveInSelected && isOverSelected && active.id !== over.id) {
            const oldIndex = selected.findIndex((item) => item.id === activeIdNum);
            const newIndex = selected.findIndex((item) => item.id.toString() === overId);
            const newSelected = arrayMove(selected, oldIndex, newIndex);
            setSelected(newSelected);
            onItemsChange(newSelected);

            return;
        }

        // Move from Available ➜ Selected
        if (!isActiveInSelected && isOverSelected) {
            const newSelected = [...selected, activeItem];
            setSelected(newSelected);
            setAvailableItems(available.filter((item) => item.id !== activeIdNum));
            onItemsChange(newSelected);

            return;
        }

        // Move from Selected ➜ Available
        if (isActiveInSelected && isOverAvailable) {
            const newSelected = selected.filter((item) => item.id !== activeIdNum);
            setSelected(newSelected);
            setAvailableItems([...available, activeItem]);
            onItemsChange(newSelected);

            return;
        }

        // Dropped on itself or the same list without change — do nothing
    };

    return (
        <>
            <ZfTypography variant="body-medium">{t('contentAdmin.dndInfoText')}</ZfTypography>
            <ZfStack direction="row" spacing="medium">
                <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <ZfStack direction="column" spacing="small" style={dndListStyle}>
                        <ZfTypography variant="body-medium" style={{ height: 'var(--zf-input-size-min-height)', alignContent: 'center' }}>
                            {t('contentAdmin.dndSelectedItems')}
                        </ZfTypography>
                        <Droppable droppableId={DroppableId.Selected}>
                            <SortableContext items={selected.map((item) => item.id.toString())} strategy={verticalListSortingStrategy}>
                                {selected.map((item) => (
                                    <Draggable key={item.id} item={item}>
                                        {itemRenderFn(item)}
                                    </Draggable>
                                ))}
                            </SortableContext>
                        </Droppable>
                    </ZfStack>
                    <ZfStack direction="column" spacing="small" style={dndListStyle}>
                        <ZfStack direction="row" alignment="center">
                            <ZfTypography variant="body-medium" style={{ flex: 1, minWidth: '50%' }}>
                                {t('contentAdmin.dndAvailableItems')}
                            </ZfTypography>
                            <ZfInput placeholder={t('contentAdmin.dndFilter')} onInputChange={onFilterChange} />
                        </ZfStack>
                        <Droppable droppableId={DroppableId.Available}>
                            {available.map((item) => (
                                <Draggable key={item.id} item={item}>
                                    {itemRenderFn(item)}
                                </Draggable>
                            ))}
                        </Droppable>
                    </ZfStack>
                    <DragOverlay>
                        <div className="dnd-item dnd-item-dragging">
                            {activeId ? itemRenderFn([...selected, ...available].find((item) => item.id.toString() === activeId.toString())!) : null}
                        </div>
                    </DragOverlay>
                </DndContext>
            </ZfStack>
        </>
    );
};
